import Tag from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { SelectOption } from '@databases/types/database';
import { selectOptionColorValues } from '@databases/utils/database/field-types/select';
import React, { FunctionComponent, useRef } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

const Container = styled.span<{
  noMargin?: boolean;
  color: string;
  backgroundColor: string;
  isMulti?: boolean;
}>`
  display: inline-flex;
  max-width: ${(props) => (props.isMulti ? '95%' : '100%')};

  && > * {
    margin: ${(props) => (props.noMargin ? '0' : '4px')};
  }

  &&& button {
    color: inherit;
  }

  &&& > span {
    color: ${(props) => props.color};
    background: ${(props) => props.backgroundColor};

    > span {
      padding-top: ${token('space.025')};
      padding-bottom: ${token('space.025')};
      font-weight: bold;
      font-size: 11px;
      line-height: 16px;
      text-transform: uppercase;
      max-width: 100%;
    }
  }
`;

export interface SelectOptionTagProps {
  option: SelectOption | undefined;
  isRemovable?: boolean;
  onRemove?: (e: React.MouseEvent) => void;
  noMargin?: boolean;
  isMulti?: boolean;
}

/**
 * Renders a single option of an Databases select field, styled as an @atlaskit/tag component.
 */
const SelectOptionTag: FunctionComponent<SelectOptionTagProps> = ({
  option,
  isRemovable = false,
  onRemove,
  noMargin = false,
  isMulti = false,
}) => {
  const { formatMessage } = useIntl();
  const isDeletedRef = useRef(false);

  const handleBeforeRemove = useFlaggingCallback(() => {
    isDeletedRef.current = true;
    return false;
  }, []);

  const handleMouseDown = useFlaggingCallback((e: React.MouseEvent) => {
    // A very hacky implementation to prevent the mousedown event from bubbling in case the remove icon was clicked,
    // to prevent it from toggling the open state of the SelectPicker.
    // Unfortunately, the mousedown event is called before onBeforeRemoveAction (which reacts to the click event),
    // so isDeletedRef.current is still false here.
    if (e.target instanceof Element && e.target.closest('[aria-label="close tag"]')) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  const handleClick = useFlaggingCallback(
    (e: React.MouseEvent) => {
      if (isDeletedRef.current) {
        onRemove?.(e);
        isDeletedRef.current = false;
      }
    },
    [onRemove]
  );

  return (
    <Container
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      noMargin={noMargin}
      color={selectOptionColorValues[option ? option.color : 'undefined'][1]}
      backgroundColor={selectOptionColorValues[option ? option.color : 'undefined'][0]}
      isMulti={isMulti}
    >
      <Tag
        text={
          option ? option.label : formatMessage(messages['board-layout.empty-tag-label'])
        }
        isRemovable={isRemovable}
        onBeforeRemoveAction={handleBeforeRemove}
        testId={`database-select-option-tag-${
          option ? option.label.replace(/\s+/g, '-').toLowerCase() : 'undefined'
        }`}
      />
    </Container>
  );
};

export default SelectOptionTag;
